// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {SelectAsync} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useMemo} from 'react';
import {useParams} from 'react-router';
import {SingleValue} from 'react-select';

import {
  mediaSliderMetricKeyRemoved,
  mediaSliderMetricKeySelected,
} from '../../../../../services/analytics/productEvents';
import {useViewer} from '../../../../../state/viewer/hooks';
import {keyDisplayName} from '../../../../../util/runs';
import {useMediaKeys} from '../../../../PanelMediaBrowser/hooks/useMediaKeys';
import {useMediaSliderKey} from '../../../../PanelMediaBrowser/hooks/useMediaSliderKey';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsDrawerContext} from '../MediaSettingsDrawerContextProvider';

type SliderKeyOption = {
  value: string | undefined;
  displayText: string;
};
const MAX_OPTIONS_DISPLAYED = 10;

export const SliderKey = () => {
  const {config, updateConfig, unsetConfigField} =
    useMediaSettingsDrawerContext();
  const currentMediaSliderKey = useMediaSliderKey(config.mediaSliderKey);

  // Tracking events related to the slider key
  const {entityName, projectName, panelId} = useParams<{
    entityName: string;
    projectName: string;
    panelId: string;
  }>();
  const user = useViewer()?.username;
  const trackMediaSliderKeyChange = useCallback(
    (updatedValue: string) => {
      const isCurrentValueDefault = currentMediaSliderKey === '_step';
      const isNewValueDefault = updatedValue === '_step';
      const eventData = {
        entityName,
        projectName,
        user,
        panelId,
      };
      if (!isNewValueDefault) {
        mediaSliderMetricKeySelected({
          ...eventData,
          metricKey: updatedValue,
        });
      } else if (!isCurrentValueDefault && isNewValueDefault) {
        mediaSliderMetricKeyRemoved({
          previousMetricKey: currentMediaSliderKey,
          ...eventData,
        });
      }
    },
    [currentMediaSliderKey, entityName, projectName, panelId, user]
  );

  // Building the options for the slider key
  const {numericKeys} = useMediaKeys();
  const sliderKeyOptions: SliderKeyOption[] = useMemo(
    () =>
      numericKeys?.map(k => ({
        value: k,
        displayText: keyDisplayName({section: 'summary', name: k}),
      })) ?? [],
    [numericKeys]
  );

  const filteredSliderKeyOptions = useCallback(
    (inputValue: string, callback: (options: SliderKeyOption[]) => void) => {
      const filtered = sliderKeyOptions
        .filter(k =>
          k.displayText.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, MAX_OPTIONS_DISPLAYED);
      callback(filtered);
    },
    [sliderKeyOptions]
  );
  const selectedOption = sliderKeyOptions.find(
    k => k.value === currentMediaSliderKey
  );
  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Slider key"
        configField="mediaSliderKey"
        unsetConfigField={() => unsetConfigField('mediaSliderKey')}
      />
      <SelectAsync<SliderKeyOption, false>
        aria-label="Select Slider Key"
        cacheOptions
        defaultOptions
        options={sliderKeyOptions}
        loadOptions={filteredSliderKeyOptions}
        formatOptionLabel={({displayText}) => (
          <div
            role="option"
            aria-selected={selectedOption?.value === displayText}>
            <span>{displayText}</span>
          </div>
        )}
        isSearchable
        value={selectedOption}
        onChange={(selection: SingleValue<SliderKeyOption>) => {
          const updatedValue = selection?.value ?? '_step';
          trackMediaSliderKeyChange(updatedValue);
          updateConfig({mediaSliderKey: updatedValue});
        }}
      />
    </div>
  );
};
